import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

class FbtBundle extends HTMLElement {
  #abortController = null;
  #mainQuantity = 1;

  connectedCallback() {
    if (this.dataset.recommendationsPerformed !== 'true') {
      this.#loadRecommendations();
      return;
    }
    if (this.dataset.hasRecommendations !== 'true') {
      this.remove();
      return;
    }

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.#syncMainQuantity();
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
    this.#syncTileStates();
    this.#updateAddButtonState();
    this.#updateTotals();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @returns {Array<{variantId: string, quantity: number}>} */
  getSelectedBundleItems() {
    return this.#getSelectedItems()
      .map((item) => ({
        variantId: String(item.dataset.variantId || ''),
        quantity: item.dataset.fbtRole === 'main' ? this.#mainQuantity : 1,
      }))
      .filter((item) => item.variantId);
  }

  async #loadRecommendations() {
    const { productId, sectionId } = this.dataset;
    if (!productId || !sectionId) {
      this.remove();
      return;
    }

    try {
      const url = new URL(this.dataset.url || '/recommendations/products', window.location.origin);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('intent', 'complementary');
      const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Product recommendations returned ${response.status}`);

      const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = Array.from(responseDocument.querySelectorAll('fbt-bundle')).find(
        (element) => element.dataset.fbtKey === this.dataset.fbtKey
      );
      if (!(replacement instanceof HTMLElement) || replacement.dataset.hasRecommendations !== 'true') {
        this.remove();
        return;
      }
      this.replaceWith(replacement);
    } catch (error) {
      console.error('Frequently bought together error:', error);
      this.remove();
    }
  }

  #getProductForm() {
    return Array.from(document.querySelectorAll('product-form-component')).find(
      (element) =>
        element instanceof HTMLElement &&
        element.dataset.productId === this.dataset.productId
    );
  }

  #syncMainQuantity() {
    const input = this.#getProductForm()?.querySelector('quantity-selector-component input[name="quantity"]');
    if (input instanceof HTMLInputElement) this.#mainQuantity = Math.max(1, Number(input.value) || 1);
  }

  #onQuantityUpdate = (event) => {
    if (event.detail?.cartLine) return;
    const form = this.#getProductForm();
    const target = /** @type {Node | null} */ (event.target);
    if (!form || !target || !form.contains(target)) return;
    this.#mainQuantity = Math.max(1, Number(event.detail?.quantity) || 1);
    this.#updateTotals();
  };

  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-fbt-checkbox]')) return;
    this.#syncTileStates();
    this.#updateAddButtonState();
    this.#updateTotals();
  };

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-fbt-add]')) {
      const form = this.#getProductForm();
      if (form && typeof /** @type {any} */ (form).addBundleToCart === 'function') {
        /** @type {any} */ (form).addBundleToCart(this.getSelectedBundleItems(), event);
      }
      return;
    }

    const scrollButton = target.closest('[data-fbt-scroll]');
    if (!(scrollButton instanceof HTMLElement)) return;
    const strip = this.querySelector('.fbt-bundle__product-strip');
    const tiles = strip?.querySelectorAll('[data-fbt-tile]');
    if (!(strip instanceof HTMLElement) || !tiles || tiles.length < 2) return;

    const offset = tiles[1].offsetLeft - tiles[0].offsetLeft;
    strip.scrollBy({ left: scrollButton.dataset.fbtScroll === 'previous' ? -offset : offset, behavior: 'smooth' });
  };

  #onVariantUpdate = (event) => {
    const productId = event.detail?.data?.newProduct?.id || event.detail?.data?.productId;
    if (productId && String(productId) !== this.dataset.productId) return;
    const variant = event.detail?.resource;
    if (!variant) return;

    const price = Number(variant.price);
    const compareAt = variant.compare_at_price && Number(variant.compare_at_price) > price ? Number(variant.compare_at_price) : price;
    this.dataset.mainPrice = String(price);
    this.dataset.mainCompare = String(compareAt);
    for (const mainItem of this.querySelectorAll('[data-fbt-role="main"]')) {
      if (mainItem instanceof HTMLElement) {
        mainItem.dataset.variantId = String(variant.id);
        mainItem.dataset.price = String(price);
        mainItem.dataset.compareAtPrice = String(compareAt);
      }
    }
    const pricesEl = this.querySelector('[data-fbt-main-prices]');
    if (pricesEl instanceof HTMLElement) pricesEl.innerHTML = this.#buildPriceHtml(price, compareAt);
    this.#updateTotals();
  };

  #buildPriceHtml(price, compareAt) {
    const money = this.#formatCents(price, this.dataset.useCurrency === 'true');
    if (compareAt <= price) return `<span class="price">${money}</span>`;
    return `<span class="compare-at-price">${this.#formatCents(compareAt, this.dataset.useCurrency === 'true')}</span><span class="price">${money}</span>`;
  }

  #formatCents(cents, withCurrency) {
    const moneyFormat = withCurrency
      ? this.dataset.moneyWithCurrencyFormat || this.dataset.moneyFormat || '{{amount}}'
      : this.dataset.moneyFormat || '{{amount}}';
    return formatMoney(cents, moneyFormat, this.dataset.currency || 'USD');
  }

  /** @returns {HTMLElement[]} */
  #getSelectedItems() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[data-fbt-item]')).filter((item) => {
        if (!(item instanceof HTMLElement)) return false;
        if (item.dataset.fbtRole === 'companion' && item.dataset.available !== 'true') return false;
        const checkbox = item.querySelector('[data-fbt-checkbox]');
        return checkbox instanceof HTMLInputElement && checkbox.checked;
      })
    );
  }

  #syncTileStates() {
    for (const tile of this.querySelectorAll('[data-fbt-tile]')) {
      if (!(tile instanceof HTMLElement)) continue;
      const productId = tile.dataset.fbtProductId;
      const item = this.querySelector(`[data-fbt-item][data-fbt-product-id="${productId}"]`);
      const checkbox = item?.querySelector('[data-fbt-checkbox]');
      tile.classList.toggle('is-selected', checkbox instanceof HTMLInputElement && checkbox.checked);
    }
  }

  #updateAddButtonState() {
    const button = this.querySelector('[data-fbt-add]');
    if (button instanceof HTMLButtonElement) button.disabled = this.getSelectedBundleItems().length === 0;
  }

  #updateTotals() {
    let totalPrice = 0;
    let totalCompare = 0;
    for (const item of this.#getSelectedItems()) {
      const quantity = item.dataset.fbtRole === 'main' ? this.#mainQuantity : 1;
      totalPrice += Number(item.dataset.price || 0) * quantity;
      totalCompare += Number(item.dataset.compareAtPrice || item.dataset.price || 0) * quantity;
    }

    const priceEl = this.querySelector('[data-fbt-total-price]');
    const compareEl = this.querySelector('[data-fbt-total-compare]');
    if (priceEl instanceof HTMLElement) priceEl.textContent = this.#formatCents(totalPrice, this.dataset.useCurrency === 'true');
    if (compareEl instanceof HTMLElement) {
      if (totalCompare > totalPrice) {
        compareEl.textContent = this.#formatCents(totalCompare, this.dataset.useCurrency === 'true');
        compareEl.classList.remove('hidden');
      } else {
        compareEl.textContent = '';
        compareEl.classList.add('hidden');
      }
    }
  }
}

if (!customElements.get('fbt-bundle')) customElements.define('fbt-bundle', FbtBundle);
